import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Checkbox from '@components/common/checkbox/main';
import Tooltip from '@components/common/tooltip/main';
import { get as getLabels } from '@api/v4/resources/labels';
import { get as getSections } from '@api/v4/brandfolders/sections';
import { getCustomFieldKeys } from '@api/v4/resources/custom_fields';

function Summary({
  ruleType,
  scope,
  actionableResources,
  actionableKeys,
  enforce,
  updateEnforce
}) {
  const [scopeResources, updateScopeResources] = useState([]);
  const [filteredActionableKeys, updatefilteredActionableKeys] = useState([]);

  const scopeType = ruleType.split("::")[1];

  const actionableType = () => {
    if (ruleType.includes("CollectionAssociation")) {
      return "Collection";
    }
    if (ruleType.includes("LabelAssociation")) {
      return "Label";
    }
    if (ruleType.includes("SectionAssociation")) {
      return "Section";
    }
    if (ruleType.includes("Watermark")) {
      return "Watermark";
    }
    return "";
  };

  useEffect(() => {
    const filteredKeys = actionableKeys.filter((actionableKey) => !!actionableKey);
    updatefilteredActionableKeys(filteredKeys);
  }, [actionableKeys]);

  useEffect(() => {
    if (scopeType === "Label") {
      getLabels().then((resources) => {
        updateScopeResources(resources);
      });
    } else if (scopeType === "Section") {
      getSections().then((resources) => {
        updateScopeResources(resources);
      });
    } else if (scopeType === "CustomField") {
      const options = { resourceKey: BFG.resource.key, resourceType: BFG.resource.type };
      getCustomFieldKeys(options).then((resources) => {
        updateScopeResources(resources);
      });
    }
  }, [scopeType]);


  const scopeText = () => {
    switch (scopeType) {
      case "Tag": {
        const shouldPluralize = scope.tag_names.length > 1;
        return (
          <>
            <p className="trigger-intro">
              {shouldPluralize ? bfTranslate("If an asset is tagged with any of the following tags:") : bfTranslate("If an asset is tagged with tag:")}
            </p>
            <div className="trigger-name-list">
              { scope.tag_names.map((name) => <p key={name}>{name}</p>) }
            </div>
          </>
        );
      }
      case "Label": {
        const labelNames = scopeResources.filter((label) => scope.label_keys.includes(label.id)).map((label) => label.attributes.name);
        const shouldPluralize = labelNames.length > 1;
        return (
          <>
            <p className="trigger-intro">
              {shouldPluralize ? bfTranslate("If an asset is added to any of the following labels:") : bfTranslate("If an asset is added to label:")}
            </p>
            <div className="trigger-name-list">
              { labelNames.map((name) => <p key={name}>{name}</p>) }
            </div>
          </>
        );
      }
      case "Section": {
        const sectionNames = scopeResources.filter((section) => scope.section_keys.includes(section.id)).map((section) => section.attributes.name);
        const shouldPluralize = sectionNames.length > 1;
        return (
          <>
            <p className="trigger-intro">
              {shouldPluralize ? bfTranslate("If an asset is added to any of the following sections:") : bfTranslate("If an asset is added to section:")}
            </p>
            <div className="trigger-name-list">
              { sectionNames.map((name) => <p key={name}>{name}</p>) }
            </div>
          </>
        );
      }
      case "CustomField": {
        const customFieldNames = scopeResources
          .filter(({ id }) => scope.custom_field_keys.includes(id))
          .map(({ attributes }) => attributes.name);
        const shouldPluralize = scope.values.length > 1;
        const customFieldValueNames = scope.values.join(', ');
        return (
          <>
            <p className="trigger-intro">
              {shouldPluralize ? bfTranslate("If an asset has custom field key and values:") : bfTranslate("If an asset has custom field and value:")}
            </p>
            <div className="trigger-name-list">
              { customFieldNames.map((name) => (
                <p key={name}>
                  {`${bfTranslate('Key')}: ${name}, ${shouldPluralize ? bfTranslate('Values') : bfTranslate('Value')}: ${customFieldValueNames}`}
                </p>
              ))}
            </div>
          </>
        );
      }
      default:
        return undefined;
    }
  };

  const actionableNames = () => actionableResources.map((resource) => {
    if (filteredActionableKeys.includes(resource.id)) {
      return resource.attributes.name;
    }
    return null;
  }).filter((name) => name !== null);

  const actionableList = () => {
    const length = filteredActionableKeys.length > 1 ? filteredActionableKeys.length : '';
    const type = filteredActionableKeys.length > 1 ? `${actionableType().toLowerCase()}s` : actionableType().toLowerCase();
    const intro = ruleType.includes("Watermark") ? `${bfTranslate(`Then add the ${type}`)}:` : `${bfTranslate(`Then add it to ${length} ${type}:`)}`;
    return (
      <div className="resource-list-container">
        <p className="actionable-intro">{intro}</p>
        <div className="actionable-name-list">
          {!ruleType.includes("Watermark") && actionableNames().map((name) => <p key={name}>{name}</p>) }
          {ruleType.includes("Watermark") && (
            <div className="brandguide-modal__filestack-container--thumbnail-container watermark-container">
              <img alt={scope.watermark_filename || ''} className="brandguide-modal__filestack-container--thumbnail--image watermark-image" src={scope.watermark_url} title={scope.watermark_filename || ''} />
            </div>
          )}
        </div>
      </div>
    );
  };

  const enforceView = () => {
    if (ruleType === "Rule::Label::CollectionAssociation") {
      return (
        <div className="enforce-container">
          <Checkbox
            checked={enforce}
            labelHtml={<div className="checkbox-text"> {bfTranslate('Enforce Reverse Action')}</div>}
            size="sm"
            toggle={() => updateEnforce(!enforce)}
          />
          <Tooltip
            tooltipContent={bfTranslate('If rule no longer applies to asset, it will be removed from selected resource(s).')}
            tooltipId="select-section-info"
          >
            <span aria-label="select section" className="bff-tool-tip icon" />
          </Tooltip>
        </div>
      );
    }
    return undefined;
  };

  return (
    <div className="summary">
      <div>{scopeText()}</div>
      <div>{actionableList()}</div>
      <div>{enforceView()}</div>
    </div>
  );
}

Summary.propTypes = {
  ruleType: PropTypes.string.isRequired,
  actionableKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  actionableResources: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string
  })).isRequired,
  scope: PropTypes.shape({
    label_keys: PropTypes.arrayOf(PropTypes.string),
    tag_names: PropTypes.arrayOf(PropTypes.string),
    section_names: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  enforce: PropTypes.bool.isRequired,
  updateEnforce: PropTypes.func.isRequired
};

export default Summary;
